import AddIcon from '@mui/icons-material/Add';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import {
    Box,
    IconButton,
    List,
    ListItemButton,
    Typography,
    alpha,
} from '@mui/material';
import { MouseEvent, memo, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { getNewAddedItemsTotalByCustomListId } from 'entities/CatalogItem/model/selectors/catalogItemSelectors';
import { addItemsToOrderIsCreating } from 'features/AddItemsToOrder/model/selectors/addItemsToOrderSelectors';
import { DynamicTooltip } from 'shared/ui/Tooltip';

import type { CustomList } from 'entities/CustomList';

interface OrderAddItemsListsRailProps {
    lists: CustomList[];
    activeListId?: string;
    onSelectList: (listId: string) => void;
    onAddAll: (list: CustomList) => void;
}

export const OrderAddItemsListsRail = memo(
    ({
        lists,
        activeListId,
        onSelectList,
        onAddAll,
    }: OrderAddItemsListsRailProps) => {
        const { t } = useTranslation();
        const addedByListId = useSelector(getNewAddedItemsTotalByCustomListId);
        const isCreatingOrder = useSelector(addItemsToOrderIsCreating);

        const sortedLists = useMemo(
            () =>
                [...lists].sort((a, b) =>
                    a.name.localeCompare(b.name, undefined, {
                        sensitivity: 'base',
                    }),
                ),
            [lists],
        );

        return (
            <Box
                sx={{
                    width: '280px',
                    flexShrink: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    minHeight: 0,
                    bgcolor: 'background.paper',
                    overflow: 'hidden',
                    borderRadius: '12px',
                }}
                data-testclass="order-add-items-lists-rail"
            >
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        padding: '16px 16px 8px',
                        flexShrink: 0,
                    }}
                >
                    <Typography variant="subtitle2">
                        {t('customLists.lists')}
                    </Typography>
                    <DynamicTooltip
                        content={t('customLists.listsInfoTooltip')}
                        placement="top"
                        containerStyle={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            lineHeight: 0,
                        }}
                    >
                        <InfoOutlinedIcon
                            sx={{
                                display: 'block',
                                fontSize: '16px',
                                color: (theme) =>
                                    alpha(theme.palette.common.black, 0.45),
                                cursor: 'help',
                            }}
                            data-testclass="order-add-items-lists-info"
                        />
                    </DynamicTooltip>
                </Box>
                <List
                    disablePadding
                    sx={{
                        overflow: 'auto',
                        flex: 1,
                        minHeight: 0,
                    }}
                >
                    {sortedLists.map((list) => {
                        const isEmpty = list.itemCount === 0;
                        const isActive = list.id === activeListId;
                        const isTouched = (addedByListId[list.id] || 0) > 0;
                        const showFill = isActive || isTouched;

                        return (
                            <ListItemButton
                                key={list.id}
                                disabled={isEmpty}
                                selected={isActive}
                                onClick={() => {
                                    if (!isEmpty) {
                                        onSelectList(list.id);
                                    }
                                }}
                                data-testclass="order-add-items-list-rail-row"
                                sx={{
                                    borderRadius: 0,
                                    minHeight: '42px',
                                    paddingTop: '8px',
                                    paddingBottom: '8px',
                                    paddingRight: '16px',
                                    paddingLeft: '16px',
                                    gap: '8px',
                                    bgcolor: showFill ? '#ecf0f8' : undefined,
                                    borderLeft: '3px solid',
                                    borderColor: isActive
                                        ? 'primary.main'
                                        : 'transparent',
                                    '&.Mui-selected': {
                                        bgcolor: '#ecf0f8',
                                    },
                                    '&.Mui-disabled': {
                                        opacity: 0.5,
                                    },
                                }}
                            >
                                <Typography
                                    variant="subtitle2"
                                    sx={{
                                        flex: 1,
                                        minWidth: 0,
                                        color: isEmpty
                                            ? (theme) =>
                                                  alpha(
                                                      theme.palette.common
                                                          .black,
                                                      0.4,
                                                  )
                                            : showFill
                                            ? 'primary.main'
                                            : 'text.primary',
                                    }}
                                    noWrap
                                >
                                    {list.name}
                                </Typography>
                                {!isEmpty ? (
                                    <IconButton
                                        size="small"
                                        disabled={isCreatingOrder}
                                        aria-label={t(
                                            'customLists.addAllItemsFromList',
                                            { name: list.name },
                                        )}
                                        onClick={(
                                            event: MouseEvent<HTMLButtonElement>,
                                        ) => {
                                            event.stopPropagation();
                                            if (isCreatingOrder) {
                                                return;
                                            }
                                            onAddAll(list);
                                        }}
                                        data-testclass="order-add-items-list-rail-add-all"
                                        sx={{
                                            width: '24px',
                                            height: '24px',
                                            padding: 0,
                                            color: '#3F6EB7',
                                            borderRadius: '4px',
                                            bgcolor: 'background.paper',
                                            ':hover': {
                                                color: '#3F6EB7',
                                                bgcolor:
                                                    'rgba(63, 110, 183, 0.1)',
                                            },
                                            '&.Mui-disabled': {
                                                color: (theme) =>
                                                    alpha(
                                                        theme.palette.common
                                                            .black,
                                                        0.26,
                                                    ),
                                                bgcolor: 'background.paper',
                                            },
                                        }}
                                    >
                                        <AddIcon
                                            sx={{
                                                width: '18px',
                                                height: '18px',
                                            }}
                                        />
                                    </IconButton>
                                ) : null}
                            </ListItemButton>
                        );
                    })}
                </List>
            </Box>
        );
    },
);

OrderAddItemsListsRail.displayName = 'OrderAddItemsListsRail';
